import { useEffect, useRef, useState } from "react";
import { Map as MapLibreMap, Marker, Popup } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@/lib/maplibreWorker";
import { Activity, AlertTriangle, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { MAP_STYLE_URL, MAP_STYLE_CONFIG_ERROR, MAP_STYLE_LOAD_ERROR_MESSAGE } from "@/lib/mapStyle";
import { pointAtDistanceAlongLine } from "@/lib/routeGeometry";

// Live Flow -- an aggregated, anonymous view of where the group of walkers currently
// is along one route, for organizers/traffic controllers. Never shows an individual
// participant: the backend (backend/server.py's "Live Flow" section) enforces
// k-anonymity by suppressing the whole response below LIVE_FLOW_MIN_ACTIVE
// participants and zeroing any segment below LIVE_FLOW_MIN_SEGMENT_COUNT -- this
// page renders exactly what it's given and adds no further inference of its own.
//
// Modeled on AdminPresence.jsx's event -> day -> route drill-down (no route param in
// the URL, since this is meant to be opened ad hoc during an event) and
// AdminIncidents.jsx's MapLibre construction recipe (worker import, style-URL
// handling, `absolute inset-0` -> plain inner div container to avoid the documented
// canvas-collapse bug).

const LIVE_FLOW_POLL_MS = 5000;

const DENSITY_NONE_COLOR = "#94a3b8"; // slate-400 -- "no data" and "suppressed" render identically, on purpose
const DENSITY_LOW_COLOR = "#16a34a"; // green
const DENSITY_MID_COLOR = "#f59e0b"; // amber
const DENSITY_HIGH_COLOR = "#dc2626"; // red

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mixHex(a, b, t) {
  const [r1, g1, b1] = hexToRgb(a);
  const [r2, g2, b2] = hexToRgb(b);
  const mix = (x, y) => Math.round(x + (y - x) * t);
  return `rgb(${mix(r1, r2)}, ${mix(g1, g2)}, ${mix(b1, b2)})`;
}

// Density is shown RELATIVE to the busiest segment on this route right now, not
// against a fixed absolute headcount -- a route with 30 participants and a route
// with 300 should both clearly show where their own busiest point is.
function colorForRelativeDensity(count, maxCount) {
  if (!count || count <= 0) return DENSITY_NONE_COLOR;
  const ratio = maxCount > 0 ? Math.min(1, count / maxCount) : 0;
  if (ratio <= 0.5) return mixHex(DENSITY_LOW_COLOR, DENSITY_MID_COLOR, ratio / 0.5);
  return mixHex(DENSITY_MID_COLOR, DENSITY_HIGH_COLOR, (ratio - 0.5) / 0.5);
}

// Builds a MapLibre `step` expression on `line-progress` (0..1 along the whole
// line) from the backend's fixed-distance segments. `step`, not `interpolate`: a
// sharp color change at each segment boundary is the correct representation of
// "here's a cluster" -- a smooth gradient between two buckets would visually invent
// data between them that was never measured.
function buildLineGradientExpression(segments, distanceMeters) {
  if (!segments?.length || !distanceMeters) {
    return DENSITY_NONE_COLOR;
  }
  const maxCount = Math.max(0, ...segments.map((s) => s.count || 0));
  const expr = ["step", ["line-progress"], colorForRelativeDensity(segments[0].count, maxCount)];
  for (let i = 1; i < segments.length; i++) {
    const ratio = Math.min(1, Math.max(0, segments[i].start_m / distanceMeters));
    expr.push(ratio, colorForRelativeDensity(segments[i].count, maxCount));
  }
  return expr;
}

function formatKm(meters) {
  if (meters == null) return "–";
  return (meters / 1000).toLocaleString("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

export default function AdminLiveFlow() {
  const { authAxios } = useAuth();

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [days, setDays] = useState([]);
  const [dayId, setDayId] = useState("");
  const [routes, setRoutes] = useState([]);
  const [routeId, setRouteId] = useState("");
  const [loadingEvents, setLoadingEvents] = useState(true);

  // `undefined` = not fetched yet (still loading); `null` = fetched, route genuinely
  // has no navigation data; an object = the real navigation payload. Collapsing the
  // first two into one falsy value would show "laden..." forever for a route with
  // no GPX/builder route at all, since the fetch legitimately resolves to `null`.
  const [navData, setNavData] = useState(undefined);
  const [flow, setFlow] = useState(null);
  const [mapError, setMapError] = useState(MAP_STYLE_CONFIG_ERROR);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const headMarkerRef = useRef(null);
  const tailMarkerRef = useRef(null);

  // Events with Live Flow actually turned on -- everything else has nothing to show.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAxios.get("/admin/events");
        if (cancelled) return;
        const items = (data.items || []).filter((e) => e.live_flow_enabled);
        setEvents(items);
        setEventId((current) => (items.some((e) => e.id === current) ? current : items[0]?.id || ""));
      } catch (error) {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authAxios]);

  useEffect(() => {
    setDays([]);
    setDayId("");
    if (!eventId) return undefined;
    let cancelled = false;
    authAxios
      .get(`/admin/events/${eventId}/days`)
      .then(({ data }) => {
        if (cancelled) return;
        const items = data.items || [];
        setDays(items);
        setDayId(items[0]?.id || "");
      })
      .catch((error) => {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [authAxios, eventId]);

  useEffect(() => {
    setRoutes([]);
    setRouteId("");
    if (!dayId) return undefined;
    let cancelled = false;
    authAxios
      .get(`/admin/event-days/${dayId}/routes`)
      .then(({ data }) => {
        if (cancelled) return;
        const items = data.items || [];
        setRoutes(items);
        setRouteId(items[0]?.id || "");
      })
      .catch((error) => {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [authAxios, dayId]);

  // Fetch the route's canonical geometry once per route selection -- never re-fetched
  // on every poll, since the route line itself doesn't change during an event.
  useEffect(() => {
    setNavData(undefined);
    setFlow(null);
    if (!routeId) return undefined;
    let cancelled = false;
    authAxios
      .get(`/admin/routes/${routeId}/navigation`)
      .then(({ data }) => {
        if (!cancelled) setNavData(data);
      })
      .catch((error) => {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [authAxios, routeId]);

  // Poll the aggregated flow endpoint as close to "live" as a plain interval allows.
  useEffect(() => {
    if (!routeId) return undefined;
    let cancelled = false;
    const poll = () => {
      authAxios
        .get(`/admin/routes/${routeId}/live-flow`)
        .then(({ data }) => {
          if (!cancelled) setFlow(data);
        })
        .catch(() => {});
    };
    poll();
    const timer = setInterval(poll, LIVE_FLOW_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [authAxios, routeId]);

  // Construct the map once per route (needs lineMetrics for line-gradient/step).
  useEffect(() => {
    if (!navData?.geojson || !mapContainerRef.current) return undefined;
    if (MAP_STYLE_CONFIG_ERROR) {
      setMapError(MAP_STYLE_CONFIG_ERROR);
      return undefined;
    }
    setMapError(null);
    const coords = navData.geojson?.geometry?.coordinates || [];
    const center = coords[Math.floor(coords.length / 2)] || coords[0];
    const map = new MapLibreMap({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: center || [5.1214, 52.0907],
      zoom: center ? 14 : 6,
    });
    mapRef.current = map;
    map.on("error", () => setMapError(MAP_STYLE_LOAD_ERROR_MESSAGE));

    const drawLine = () => {
      if (!mapRef.current || mapRef.current.getSource("live-flow-route-line")) return;
      mapRef.current.addSource("live-flow-route-line", {
        type: "geojson",
        data: navData.geojson,
        lineMetrics: true,
      });
      mapRef.current.addLayer({
        id: "live-flow-route-line",
        type: "line",
        source: "live-flow-route-line",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-width": 6, "line-gradient": DENSITY_NONE_COLOR },
      });
    };
    if (map.isStyleLoaded()) drawLine();
    else map.on("load", drawLine);

    return () => {
      map.remove();
      mapRef.current = null;
      headMarkerRef.current = null;
      tailMarkerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navData?.geojson]);

  // Re-color the line + move the head/tail markers on every poll. Only
  // setPaintProperty/marker moves -- never setData(), so a 5s poll is cheap.
  useEffect(() => {
    const map = mapRef.current;
    const coords = navData?.geojson?.geometry?.coordinates;
    if (!map || !coords?.length) return;
    const applyPaint = () => {
      if (!map.getLayer("live-flow-route-line")) return;
      const distanceMeters = navData.distance_meters || 0;
      if (!flow || flow.suppressed) {
        map.setPaintProperty("live-flow-route-line", "line-gradient", DENSITY_NONE_COLOR);
        if (headMarkerRef.current) {
          headMarkerRef.current.remove();
          headMarkerRef.current = null;
        }
        if (tailMarkerRef.current) {
          tailMarkerRef.current.remove();
          tailMarkerRef.current = null;
        }
        return;
      }
      map.setPaintProperty(
        "live-flow-route-line",
        "line-gradient",
        buildLineGradientExpression(flow.segments, distanceMeters)
      );

      const headPoint = pointAtDistanceAlongLine(coords, flow.head_distance_m);
      const tailPoint = pointAtDistanceAlongLine(coords, flow.tail_distance_m);
      if (headPoint) {
        if (!headMarkerRef.current) {
          headMarkerRef.current = new Marker({ color: "#16a34a" })
            .setPopup(new Popup({ offset: 12 }).setText("Kop van de stoet"))
            .addTo(map);
        }
        headMarkerRef.current.setLngLat(headPoint);
      }
      if (tailPoint) {
        if (!tailMarkerRef.current) {
          tailMarkerRef.current = new Marker({ color: "#dc2626" })
            .setPopup(new Popup({ offset: 12 }).setText("Staart van de stoet"))
            .addTo(map);
        }
        tailMarkerRef.current.setLngLat(tailPoint);
      }
    };
    if (map.isStyleLoaded()) applyPaint();
    else map.once("load", applyPaint);
  }, [flow, navData]);

  const selectedRoute = routes.find((r) => r.id === routeId);

  return (
    <div data-testid="admin-live-flow-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-svj-orange via-svj-pink to-svj-purple flex items-center justify-center">
          <Activity className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-white">Live Flow</h1>
          <p className="text-sm text-slate-400">
            Geaggregeerde, anonieme routestroom -- nooit een individuele deelnemer.
          </p>
        </div>
      </div>

      {loadingEvents && (
        <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Events laden…
        </div>
      )}

      {!loadingEvents && events.length === 0 && (
        <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400">
          Er is nog geen event waarvoor Live Flow actief is. Zet "Live Flow" aan bij
          een StapKompas-event (tabblad Instellingen) om deze pagina te gebruiken.
        </div>
      )}

      {!loadingEvents && events.length > 0 && (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-4">
            {events.length > 1 && (
              <label className="block max-w-xs flex-1 min-w-[200px]">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Event</span>
                <select
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  data-testid="live-flow-event-select"
                  className="mt-1 w-full h-11 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                >
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>{event.title}</option>
                  ))}
                </select>
              </label>
            )}
            {days.length > 1 && (
              <label className="block max-w-xs flex-1 min-w-[160px]">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Dag</span>
                <select
                  value={dayId}
                  onChange={(e) => setDayId(e.target.value)}
                  data-testid="live-flow-day-select"
                  className="mt-1 w-full h-11 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                >
                  {days.map((day) => (
                    <option key={day.id} value={day.id}>{day.label || `Dag ${day.day_number}`}</option>
                  ))}
                </select>
              </label>
            )}
            {routes.length > 1 && (
              <label className="block max-w-xs flex-1 min-w-[160px]">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Route</span>
                <select
                  value={routeId}
                  onChange={(e) => setRouteId(e.target.value)}
                  data-testid="live-flow-route-select"
                  className="mt-1 w-full h-11 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                >
                  {routes.map((route) => (
                    <option key={route.id} value={route.id}>
                      {route.title}{route.distance_km ? ` (${route.distance_km} km)` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {routes.length > 0 && !routeId && (
            <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400">
              Geen routes gevonden voor deze dag.
            </div>
          )}

          {routeId && navData === undefined && (
            <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400 inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Route laden…
            </div>
          )}

          {routeId && navData === null && (
            <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400" data-testid="live-flow-no-navigation">
              Deze route heeft geen navigatiegegevens (geen GPX of routebouwer-route),
              dus is er geen kaart om de stroom op te tonen.
            </div>
          )}

          {routeId && navData && (
            <div className="rounded-2xl border border-svj-border bg-svj-card overflow-hidden">
              <div className="relative h-[420px]">
                <div className="absolute inset-0">
                  <div ref={mapContainerRef} className="w-full h-full" data-testid="admin-live-flow-map" />
                </div>
                {mapError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-svj-bg p-3">
                    <div className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 max-w-sm">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {mapError}
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-svj-border">
                {!flow && (
                  <div className="text-sm text-slate-400 inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Live data laden…
                  </div>
                )}
                {flow?.suppressed && (
                  <div className="text-sm text-slate-400" data-testid="live-flow-suppressed">
                    Nog te weinig actieve deelnemers op deze route om iets te tonen —
                    dit voorkomt dat een klein aantal mensen individueel herkenbaar
                    wordt.
                  </div>
                )}
                {flow && !flow.suppressed && (
                  <div className="space-y-1" data-testid="live-flow-summary">
                    <div className="flex items-center gap-2 text-sm text-white font-semibold">
                      <Users className="h-4 w-4 text-svj-cyan" /> {flow.active_count} actieve deelnemers op {selectedRoute?.title}
                    </div>
                    <div className="text-xs text-slate-400">
                      Kop bij km {formatKm(flow.head_distance_m)} · Staart bij km {formatKm(flow.tail_distance_m)} ·{" "}
                      {formatKm(flow.head_distance_m - flow.tail_distance_m)} km tussen kop en staart
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
